import React, { useEffect, useState } from 'react';
import { PIPELINES_AND_STEPS_PATHS } from 'constants/pipelines-and-steps-paths';
import { Controller, useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    createFieldMappingConfig,
    CreateFieldMappingDefaults,
    CreateFieldMappingSchema,
    createFieldMappingStyles,
    FieldMappingFormValues,
} from './field-mapping-form-helper';
import { CreateFormFeatureDialog } from 'components';
import { pipelineFormStyles as formStyles } from 'pages/pipelines/CreateOrUpdatePipeline/components/pipeline-form-helper';
import { Box, Grid } from '@mui/material';
import { yupResolver } from '@hookform/resolvers/yup';
import { Divider, TextField as MuiTextField } from '@biq/apex-ui';
import { createPipelineStyles as createFormFeatureStyles } from 'pages/pipelines/CreateOrUpdatePipeline/create-or-update-pipeline-helper';
import { FIELD_MAPPING } from 'pages/pipelines/pipelines-helper';
import { FieldNameType } from '../components/FieldNameType';
import { CreateStepFormData } from '../components/step-form-helper';
import { CUSTOM, FROM_DICTIONARY } from '../components/field-name-type-helper';
import { PostFieldMappingStepProps, useCreateFieldMapping } from 'hooks';
import { useAuth, useServicesEnabled } from '../../../../core';

export const FieldMappingForm = ({
    isEdit = false,
    stepData,
    onModalClose,
    onAddNewOrUpdateFieldMapping,
    fieldMappingDetails,
}: {
    isEdit: boolean;
    stepData?: CreateStepFormData;
    onModalClose: () => void;
    onAddNewOrUpdateFieldMapping?: (map: Record<string, string>) => void;
    fieldMappingDetails?: Record<string, string>;
}): React.JSX.Element => {
    const { isServiceEnabledFor } = useServicesEnabled();
    const isDataPipelineServiceEnabled = isServiceEnabledFor('DATA_PIPELINE');
    const navigate = useNavigate();
    const location = useLocation();
    const {
        control,
        watch,
        setValue,
        clearErrors,
        handleSubmit,
        formState: { errors },
    } = useForm<FieldMappingFormValues>({
        mode: 'all',
        resolver: yupResolver(CreateFieldMappingSchema),
        defaultValues: CreateFieldMappingDefaults,
    });
    const { getToken } = useAuth();
    const { mutate: createFieldMapping } = useCreateFieldMapping();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [fieldMappingListConfig, setFieldMappingListConfig] = useState(
        createFieldMappingConfig
    );

    useEffect(() => {
        if (isEdit) {
            const editFieldMappingConfig = {
                'field-mapping': {
                    ...fieldMappingListConfig['field-mapping'],
                    headingText: 'Edit Field Mapping',
                },
            };
            setFieldMappingListConfig(editFieldMappingConfig);
        }
    }, [isEdit, fieldMappingDetails]);

    useEffect(() => {
        if (fieldMappingDetails) {
            setValue('inboundName', Object.keys(fieldMappingDetails)[0]);
        }
    }, [fieldMappingDetails]);

    const handleBackButtonClick = (): void => {
        onModalClose?.();
    };

    const handleCloseButtonClick = (): void => {
        if (location.pathname.includes('steps/edit')) {
            onModalClose();
        } else {
            navigate(PIPELINES_AND_STEPS_PATHS.LANDING_PAGE);
        }
    };

    const onSubmit = (data: FieldMappingFormValues): void => {
        if (!data) return;
        let mappedFieldName: string | null = '';

        if (data.fieldNameType === CUSTOM) {
            mappedFieldName = data.customFieldName ?? null;
        } else if (data.fieldNameType === FROM_DICTIONARY) {
            mappedFieldName = data.fieldName ?? null;
        }

        if (location.pathname.includes('step-details')) {
            setIsLoading(true);
            const postData: PostFieldMappingStepProps = {
                name: stepData?.name ?? '',
                description: stepData?.description ?? '',
                map: {
                    [data.inboundName]: mappedFieldName,
                },
            };

            createFieldMapping(
                { postData, token: getToken() },
                {
                    onSuccess: (result: any) => {
                        setIsLoading(false);
                        const path =
                            PIPELINES_AND_STEPS_PATHS.UPDATE_STEP_DETAILS.replace(
                                ':id',
                                result.data.id
                            );
                        const query = `?option=${FIELD_MAPPING}`;
                        onModalClose();
                        navigate(`${path}${query}`);
                    },
                    onError: () => {
                        setIsLoading(false);
                    },
                }
            );
        } else if (location.pathname.includes('steps/edit')) {
            onAddNewOrUpdateFieldMapping?.({
                [data.inboundName]: mappedFieldName ?? '',
            });
            onModalClose();
        }
    };

    if (!isDataPipelineServiceEnabled) {
        navigate(PIPELINES_AND_STEPS_PATHS.LANDING_PAGE);
        return <></>;
    }

    return (
        <CreateFormFeatureDialog
            buttonText={isEdit ? 'Update' : 'Save'}
            isLoading={isLoading}
            handleSubmit={handleSubmit(onSubmit)}
            handleCloseButton={handleCloseButtonClick}
            handleBackButton={handleBackButtonClick}
            listConfig={fieldMappingListConfig}
            isOpenInModal={true}
            customStyles={createFormFeatureStyles.root}
            customButtonContainerStyles={
                createFieldMappingStyles.formFeatureNextButtonStyles
            }
        >
            <Grid sx={formStyles.mainContainer}>
                <Box
                    sx={{
                        ...formStyles.fieldGroup,
                        ...createFieldMappingStyles.formFieldHeight,
                    }}
                >
                    <Controller
                        name='inboundName'
                        control={control}
                        render={({ field }): React.JSX.Element => (
                            <MuiTextField
                                {...field}
                                variant='outlined'
                                label='Inbound Name'
                                value={field.value}
                                onChange={(event): void => {
                                    field.onChange(event);
                                }}
                                onBlur={(value): void => {
                                    field.onChange(value.target.value.trim());
                                }}
                                required
                                error={!!errors?.inboundName}
                                errorText={errors?.inboundName?.message ?? ''}
                            />
                        )}
                    />
                </Box>

                <Divider sx={createFieldMappingStyles.divider} />

                <FieldNameType
                    control={control}
                    errors={errors}
                    watch={watch}
                    setValue={setValue}
                    clearErrors={clearErrors}
                />
            </Grid>
        </CreateFormFeatureDialog>
    );
};
